import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Activity, Thermometer, Heart, Wind, Waves } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { AddTrackerEntryModal } from "@/components/AddTrackerEntryModal";
import type { HealthMetric } from "@/types/tracker";
import type { User } from "@/types";

interface AddVitalsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointmentId: string;
  patientId: string;
}

type VitalType =
  | "bp"
  | "heart_rate"
  | "temperature"
  | "respiratory_rate"
  | "blood_glucose"
  | null;

const vitalOptions = [
  { key: "bp" as const, icon: Activity, label: "BP", sub: "Blood Pressure" },
  {
    key: "heart_rate" as const,
    icon: Heart,
    label: "Heart Rate",
    sub: "Pulse",
  },
  {
    key: "temperature" as const,
    icon: Thermometer,
    label: "Temperature",
    sub: "Body Temp",
  },
  {
    key: "respiratory_rate" as const,
    icon: Wind,
    label: "Respiratory Rate",
    sub: "Breaths/min",
  },
  {
    key: "blood_glucose" as const,
    icon: Waves,
    label: "Blood Glucose",
    sub: "Sugar Level",
  },
] as const;

// Mapping from vital type selector to the HealthMetric id used by AddTrackerEntryModal
const vitalTypeToMetric: Record<Exclude<VitalType, null>, HealthMetric> = {
  bp: {
    id: "blood-pressure",
    title: "Blood Pressure",
    value: "",
    unit: "mmHg",
    period: "",
    category: "heart-health",
  },
  heart_rate: {
    id: "heart-rate",
    title: "Heart Rate",
    value: "",
    unit: "Beats/Min",
    period: "",
    category: "heart-health",
  },
  temperature: {
    id: "temperature",
    title: "Temperature",
    value: "",
    unit: "°F",
    period: "",
    category: "all",
  },
  respiratory_rate: {
    id: "respiratory-rate",
    title: "Respiratory Rate",
    value: "",
    unit: "BPM",
    period: "",
    category: "respiratory-rate",
  },
  blood_glucose: {
    id: "blood-glucose",
    title: "Blood Glucose",
    value: "",
    unit: "mg/dL",
    period: "",
    category: "diabetes",
  },
};

export const AddVitalsDialog = ({
  open,
  onOpenChange,
  appointmentId,
  patientId,
}: AddVitalsDialogProps) => {
  const [selectedVital, setSelectedVital] = useState<VitalType>(null);
  const queryClient = useQueryClient();

  // Build a minimal User object for AddTrackerEntryModal
  const selectedPatient: User | null = patientId
    ? ({
        id: patientId,
        first_name: "",
        last_name: "",
        phone_number: "",
        email: null,
        profile_pic: null,
        age: 0,
        gender: "",
        zarity_user_id: "",
        city: "",
      } as User)
    : null;

  const handleClose = () => {
    setSelectedVital(null);
    onOpenChange(false);
  };

  const handleTrackerModalClose = () => {
    setSelectedVital(null);
  };

  const handleDataSubmitted = () => {
    // Invalidate the vitalsHome query so the consultation vitals card refreshes
    queryClient.invalidateQueries({
      queryKey: ["vitalsHome", appointmentId],
    });
    setSelectedVital(null);
    onOpenChange(false);
  };

  const handleDataSubmissionError = (_errorMessage: string) => {
    // Error is already handled within AddTrackerEntryModal
    // We just keep the modal open so the user can retry
  };

  // If a vital type is selected, show the AddTrackerEntryModal directly
  if (selectedVital) {
    const metric = vitalTypeToMetric[selectedVital];
    return (
      <AddTrackerEntryModal
        isOpen={true}
        onClose={handleTrackerModalClose}
        metric={metric}
        onSubmit={() => {}}
        selectedPatient={selectedPatient}
        onDataSubmitted={handleDataSubmitted}
        onDataSubmissionError={handleDataSubmissionError}
        darkMode
      />
    );
  }

  // Show the vital type selector
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md flex flex-col p-0 gap-0 border-border/60">
        <DialogHeader className="p-6 pb-0 shrink-0">
          <DialogTitle className="z-h2 text-foreground">Add Vitals</DialogTitle>
        </DialogHeader>
        <Separator className="mt-4" />

        <div className="grid grid-cols-2 gap-3 p-6">
          {vitalOptions.map(({ key, icon: Icon, label, sub }) => (
            <button
              key={key}
              onClick={() => setSelectedVital(key)}
              className="flex flex-col items-start p-4 rounded-xl border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all text-left"
            >
              <div className="mb-3 p-2 bg-primary/10 text-primary rounded-lg">
                <Icon className="w-5 h-5" />
              </div>
              <div className="z-body-md text-foreground">{label}</div>
              <div className="z-caption text-muted-foreground">{sub}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
